/**
 * Types de l'API INTERNE consommée par le back-office.
 *
 * `@genviz/shared/dto` (domaine `admin`) couvre les quatre endpoints déjà contractualisés dans
 * docs/02-contrat-api.md : import de stock, recherche de stock, désassociation forcée,
 * diagnostic d'appareil — plus le journal d'audit. Ce fichier ajoute ce que le back-office
 * exige et que le contrat public ne décrit pas encore, parce que le 2e livrable n'a pas été
 * spécifié écran par écran :
 *
 *   - la session STAFF (le contrat mobile n'authentifie que des exploitants) ;
 *   - le tableau de bord d'exploitation (parc, ingestion MQTT) ;
 *   - le cycle de vie du stock (mise en SAV, mise hors service) ;
 *   - les exploitations vues de l'intérieur, et leur suspension ;
 *   - la carte inter-exploitations.
 *
 * Chaque bloc porte la mention HYPOTHÈSE avec le chemin retenu : ce sont des propositions de
 * contrat à confronter au backend, pas des endpoints existants. Elles respectent les mêmes
 * règles que le reste du contrat : aucune chaîne d'affichage, aucun identifiant d'écran,
 * horodatages ISO 8601 UTC, valeurs numériques avec l'unité dans le nom du champ.
 */

import type {
  Curseur,
  EmpriseGeo,
  IsoDateTime,
  Kilovolts,
  Latitude,
  Longitude,
  PointGeo,
  RefExploitation,
  Secondes,
  Uuid,
} from '@genviz/shared/common';
import type {
  CriticiteAlerte,
  EtatAlimentation,
  EtatAppareil,
  EtatConnectivite,
  ModeleAppareil,
  RoleInterne,
  RoleUtilisateur,
  StatutExploitation,
  StatutMembre,
  StatutStockAppareil,
} from '@genviz/shared/enums';
import type {
  CadranTension,
  FicheStockAppareil,
  JetonsAuthentification,
  OperateurInterne,
  PointTension,
  ResumeAlerte,
} from '@genviz/shared/dto';

/* -------------------------------------------------------------------------- */
/* Session STAFF                                                               */
/* HYPOTHÈSE — POST /internal/v1/auth/login | /refresh | /logout, GET /me      */
/* -------------------------------------------------------------------------- */

/**
 * Le rôle STAFF n'est jamais attribuable depuis le mobile (D-09) et n'emprunte donc pas
 * `POST /v1/auth/login`. Les jetons restent OPAQUES et indexés en Redis (docs/06) : le
 * back-office ne lit AUCUN claim, il ne connaît que la durée de vie annoncée.
 */
export interface ConnexionStaffRequete {
  email: string;
  mot_de_passe: string;
}

export interface ConnexionStaffReponse {
  jetons: JetonsAuthentification;
  operateur: OperateurInterne;
}

export interface RafraichissementStaffRequete {
  refresh_token: string;
}

export interface RafraichissementStaffReponse {
  jetons: JetonsAuthentification;
}

export interface DeconnexionStaffRequete {
  refresh_token: string;
}

/* -------------------------------------------------------------------------- */
/* Tableau de bord                                                             */
/* HYPOTHÈSE — GET /internal/v1/dashboard                                      */
/* -------------------------------------------------------------------------- */

/** Un point de la série d'ingestion. Le pas est porté par `pas_min` de la réponse. */
export interface PointIngestion {
  t: IsoDateTime;
  /** Trames acceptées sur le pas. */
  messages: number;
  /** Trames rejetées sur le pas (schéma invalide, rejeu, identité divergente). */
  rejets: number;
}

/** Santé de la chaîne d'ingestion — le premier bloc qu'on regarde quand ça va mal. */
export interface EtatIngestion {
  /** Vrai si le consommateur MQTT est abonné et le broker joignable. */
  broker_connecte: boolean;
  /** Nombre d'objets actuellement connectés au broker (sessions MQTT ouvertes). */
  objets_connectes: number;
  /** Débit courant, moyenné sur les 5 dernières minutes. */
  messages_par_minute: number;
  /** Horodatage de la dernière trame ingérée, tous appareils confondus. */
  dernier_message_at: IsoDateTime | null;
  /** Trames rejetées sur la dernière heure. */
  rejets_1h: number;
  /** Retard de traitement de la file d'ingestion, en secondes. */
  retard_traitement_s: Secondes;
  /** Série des 60 dernières minutes, ordre chronologique croissant. */
  serie: PointIngestion[];
  /** Pas de la série, en minutes. */
  pas_min: number;
}

export interface EtatParc {
  /** Appareils appairés à une exploitation, tous statuts métier confondus. */
  total_appaires: number;
  par_etat: Record<EtatAppareil, number>;
  par_statut_stock: Record<StatutStockAppareil, number>;
  hors_ligne: number;
  /** Appareils n'ayant jamais publié la moindre trame depuis leur appairage. */
  jamais_vus: number;
}

export interface EtatAlertes {
  critiques_actives: number;
  attention_actives: number;
  /** Alertes critiques ouvertes depuis plus de 24 h : le vrai signal de dérive. */
  critiques_agees_24h: number;
  ouvertes_par_criticite: Record<CriticiteAlerte, number>;
}

export interface EtatExploitations {
  total: number;
  actives: number;
  suspendues: number;
  /** Créées sur les 7 derniers jours. */
  nouvelles_7j: number;
}

/** Appareil muet, listé en clair sur le tableau de bord pour éviter un second écran. */
export interface AppareilMuet {
  numero_serie_electrificateur: string;
  nom: string | null;
  exploitation: RefExploitation | null;
  dernier_message_at: IsoDateTime | null;
  statut_metier: EtatAppareil;
}

export interface TableauDeBordReponse {
  parc: EtatParc;
  alertes: EtatAlertes;
  exploitations: EtatExploitations;
  ingestion: EtatIngestion;
  /** Les appareils silencieux depuis le plus longtemps, au plus 10. */
  appareils_muets: AppareilMuet[];
  /** Dernières alertes critiques du parc, toutes exploitations confondues. */
  dernieres_alertes_critiques: ResumeAlerte[];
  /** Horloge serveur : toutes les fraîcheurs de l'écran s'y réfèrent. */
  horloge_serveur: IsoDateTime;
}

/* -------------------------------------------------------------------------- */
/* Stock — cycle de vie                                                        */
/* HYPOTHÈSE — POST /internal/v1/stock/{sn}/status                             */
/* -------------------------------------------------------------------------- */

/**
 * Transitions autorisées du cycle de vie, appliquées côté serveur ; la table est reprise ici
 * pour n'afficher à l'opérateur que ce qui aboutira. Un appareil ENREGISTRE ne quitte pas cet
 * état par ce chemin : il faut passer par la désassociation forcée, qui annule les commandes
 * en vol et régénère le secret d'appairage.
 */
export const TRANSITIONS_STOCK: Readonly<Record<StatutStockAppareil, StatutStockAppareil[]>> = {
  EN_STOCK: ['SAV', 'HORS_SERVICE'],
  ENREGISTRE: [],
  RETIRE: ['EN_STOCK', 'SAV', 'HORS_SERVICE'],
  SAV: ['EN_STOCK', 'HORS_SERVICE'],
  HORS_SERVICE: [],
};

export interface ChangementStatutStockRequete {
  statut: StatutStockAppareil;
  /** Motif de traçabilité, consigné au journal d'audit. 200 caractères max. */
  motif: string;
}

export interface ChangementStatutStockReponse {
  numero_serie_electrificateur: string;
  statut: StatutStockAppareil;
  executee_at: IsoDateTime;
}

/* -------------------------------------------------------------------------- */
/* Mesures récentes d'un appareil                                              */
/* HYPOTHÈSE — GET /internal/v1/devices/{sn}/measures                          */
/* -------------------------------------------------------------------------- */

/**
 * Équivalent interne de `GET /v1/devices/{id}/voltage-history`, mais adressé par numéro de
 * série et sans exiger d'appartenance à une exploitation : le support diagnostique un appareil
 * qu'il ne « possède » pas.
 */
export interface MesuresRecentesRequete {
  depuis?: IsoDateTime;
  max_points?: number;
}

export interface MesuresRecentesReponse {
  debut_at: IsoDateTime;
  fin_at: IsoDateTime;
  points: PointTension[];
  /** Bornes et seuils à tracer : identiques au cadran affiché sur le mobile. */
  reperes: CadranTension;
  derniere_tension_kv: Kilovolts | null;
  derniere_mesure_at: IsoDateTime | null;
}

/* -------------------------------------------------------------------------- */
/* Exploitations                                                               */
/* HYPOTHÈSE — GET /internal/v1/exploitations[/{id}], POST .../status          */
/* -------------------------------------------------------------------------- */

export interface ListeExploitationsRequete {
  /** Recherche sur le nom (normalisé) ou l'e-mail de l'administrateur. */
  q?: string;
  statut?: StatutExploitation;
  curseur?: Curseur;
  limite?: number;
}

export interface LigneExploitation {
  id: Uuid;
  nom: string;
  statut: StatutExploitation;
  nb_utilisateurs: number;
  nb_electrificateurs: number;
  /** Appareils dont l'état métier est CRITIQUE au moment de la réponse. */
  nb_appareils_critiques: number;
  administrateur_email: string | null;
  created_at: IsoDateTime;
  /** Dernière trame reçue d'un appareil de l'exploitation, tous appareils confondus. */
  derniere_activite_at: IsoDateTime | null;
}

export interface ListeExploitationsReponse {
  items: LigneExploitation[];
  total: number;
  curseur_suivant: Curseur | null;
}

/** Membre d'une exploitation, vu du back-office (jamais « est_moi » : le STAFF n'en est pas). */
export interface MembreBackOffice {
  id: Uuid | null;
  prenom: string;
  nom: string;
  email: string;
  role: RoleUtilisateur;
  statut: StatutMembre;
  membre_depuis: IsoDateTime | null;
  derniere_connexion_at: IsoDateTime | null;
}

/** Appareil d'une exploitation, vu du back-office. */
export interface AppareilExploitation {
  id: Uuid;
  nom: string;
  numero_serie_electrificateur: string;
  numero_serie_module: string;
  modele: ModeleAppareil;
  statut_metier: EtatAppareil;
  connectivite: EtatConnectivite;
  etat_alimentation: EtatAlimentation;
  firmware_version: string | null;
  dernier_message_at: IsoDateTime | null;
  alertes_actives: number;
}

export interface DetailExploitationReponse {
  id: Uuid;
  nom: string;
  statut: StatutExploitation;
  fuseau_horaire_defaut: string;
  created_at: IsoDateTime;
  /** Renseigné quand l'exploitation est suspendue. */
  suspendue_at: IsoDateTime | null;
  motif_suspension: string | null;
  administrateur: MembreBackOffice | null;
  utilisateurs: MembreBackOffice[];
  appareils: AppareilExploitation[];
  nb_alertes_actives: number;
  nb_invitations_en_attente: number;
}

/**
 * Suspension d'une exploitation : les comptes ne peuvent plus se connecter, les objets
 * continuent de publier (on ne coupe jamais l'ingestion — l'historique d'un litige doit
 * rester complet) et les notifications sortantes sont suspendues.
 */
export interface ChangementStatutExploitationRequete {
  statut: Extract<StatutExploitation, 'ACTIVE' | 'SUSPENDUE'>;
  motif: string;
}

export interface ChangementStatutExploitationReponse {
  id: Uuid;
  statut: StatutExploitation;
  sessions_revoquees: number;
  executee_at: IsoDateTime;
}

/** Désactivation/réactivation d'un compte utilisateur par le support. */
export interface ChangementStatutUtilisateurRequete {
  actif: boolean;
  motif: string;
}

export interface ChangementStatutUtilisateurReponse {
  id: Uuid;
  statut: StatutMembre;
  sessions_revoquees: number;
  executee_at: IsoDateTime;
}

/* -------------------------------------------------------------------------- */
/* Supervision cartographique                                                  */
/* HYPOTHÈSE — GET /internal/v1/supervision/map                                */
/* -------------------------------------------------------------------------- */

export interface SupervisionCarteRequete {
  /** Filtre facultatif sur une exploitation. */
  exploitation_id?: Uuid;
  /** Ne renvoie que les appareils dans ces états métier. */
  etats?: EtatAppareil[];
}

/** Marqueur inter-exploitations : identique au marqueur mobile, plus le propriétaire. */
export interface MarqueurSupervision {
  id: Uuid;
  nom: string;
  numero_serie_electrificateur: string;
  exploitation: RefExploitation;
  lat: Latitude;
  lon: Longitude;
  statut_metier: EtatAppareil;
  connectivite: EtatConnectivite;
  etat_alimentation: EtatAlimentation;
  alertes_actives: number;
  position_at: IsoDateTime | null;
  dernier_message_at: IsoDateTime | null;
}

export interface SupervisionCarteReponse {
  appareils: MarqueurSupervision[];
  barycentre: PointGeo | null;
  emprise: EmpriseGeo | null;
  /** Appareils appairés sans aucune position connue : invisibles sur la carte, comptés ici. */
  sans_position: number;
  genere_at: IsoDateTime;
}

/* -------------------------------------------------------------------------- */
/* Recherche transverse                                                        */
/* -------------------------------------------------------------------------- */

/** Résultat de la barre de recherche globale (numéro de série, exploitation, e-mail). */
export interface ResultatRecherche {
  type: 'APPAREIL' | 'EXPLOITATION' | 'UTILISATEUR';
  /** Numéro de série pour un appareil, UUID sinon. */
  reference: string;
  intitule: string;
  detail: string | null;
}

/* -------------------------------------------------------------------------- */
/* Réexports de confort                                                        */
/* -------------------------------------------------------------------------- */

export type { FicheStockAppareil, OperateurInterne, RoleInterne };

/* -------------------------------------------------------------------------- */
/* Exploitation commerciale — clients, consommation, abonnements, SAV          */
/* GET  /internal/v1/clients | /consommation | /abonnements | /sav/tickets     */
/* -------------------------------------------------------------------------- */

export type FormuleSms = 'ESSAI' | 'ESSENTIEL' | 'CONFORT' | 'ILLIMITE';
export type StatutAbonnement = 'ESSAI' | 'ACTIF' | 'SUSPENDU' | 'RESILIE' | 'IMPAYE';
export type StatutTicket = 'OUVERT' | 'EN_COURS' | 'EN_ATTENTE_CLIENT' | 'RESOLU' | 'FERME';
export type PrioriteTicket = 'BASSE' | 'NORMALE' | 'HAUTE' | 'CRITIQUE';

/** Abonnement tel qu'il apparaît sur la ligne d'un client. */
export interface AbonnementResume {
  id: Uuid;
  formule: FormuleSms;
  statut: StatutAbonnement;
  /** `null` pour la formule illimitée. */
  quota_mensuel: number | null;
  sms_consommes_mois: number;
  prix_mensuel_cents: number;
}

/** Une ligne de la vue Clients : tout ce qu'un commercial ou un support veut voir d'un coup. */
export interface LigneClient {
  id: Uuid;
  nom: string;
  statut: StatutExploitation;
  created_at: IsoDateTime | null;
  nb_utilisateurs: number;
  nb_electrificateurs: number;
  nb_appareils_critiques: number;
  nb_appareils_hors_ligne: number;
  administrateur_email: string | null;
  administrateur_telephone: string | null;
  derniere_activite_at: IsoDateTime | null;
  /** Volume échangé sur 30 jours glissants, montant + descendant. */
  octets_30j: number;
  sav_ouverts: number;
  abonnement: AbonnementResume | null;
}

export interface ListeClientsReponse {
  items: LigneClient[];
  total: number;
}

export interface PointConsommation {
  jour: string;
  octets_montants: number;
  octets_descendants: number;
  trames: number;
}

export interface ConsommationReponse {
  fenetre_jours: number;
  total: {
    octets_montants: number;
    octets_descendants: number;
    octets_total: number;
    trames_montantes: number;
    trames_rejetees: number;
    appareils_actifs: number;
    /** Le seul chiffre directement comparable à un forfait de carte SIM. */
    octets_moyens_par_appareil_jour: number;
  };
  serie: PointConsommation[];
  par_appareil: {
    numero_serie_electrificateur: string;
    nom: string | null;
    exploitation_nom: string | null;
    octets: number;
    trames: number;
    trames_rejetees: number;
  }[];
  par_client: { id: Uuid; nom: string; octets: number; appareils: number }[];
  genere_at: IsoDateTime;
}

export interface Abonnement extends AbonnementResume {
  exploitation_id: Uuid;
  exploitation_nom: string | null;
  /** `null` pour la formule illimitée : un pourcentage n'a pas de sens sans borne. */
  quota_utilise_pct: number | null;
  debut_at: IsoDateTime | null;
  fin_at: IsoDateTime | null;
  renouvellement_auto: boolean;
  envois_mois: number;
  cout_operateur_mois_cents: number;
  motif_dernier_statut: string | null;
  created_at: IsoDateTime | null;
}

export interface ListeAbonnementsReponse {
  items: Abonnement[];
  total: number;
  /** Revenu récurrent mensuel, somme des abonnements ACTIF. */
  revenu_mensuel_cents: number;
  formules: Record<FormuleSms, { quota: number | null; prix_cents: number; duree_jours: number | null }>;
}

export interface SouscriptionRequete {
  exploitation_id: Uuid;
  formule: FormuleSms;
}

export interface ChangementStatutAbonnementRequete {
  statut: StatutAbonnement;
  motif: string;
}

export interface LigneTicket {
  id: Uuid;
  numero: number;
  sujet: string;
  statut: StatutTicket;
  priorite: PrioriteTicket;
  canal: string | null;
  created_at: IsoDateTime | null;
  premiere_reponse_at: IsoDateTime | null;
  resolu_at: IsoDateTime | null;
  exploitation: RefExploitation | null;
  demandeur: { prenom: string | null; nom: string | null; email: string } | null;
  appareil_nom: string | null;
  nb_messages: number;
}

export interface ListeTicketsReponse {
  items: LigneTicket[];
  statistiques: {
    ouverts: number;
    en_cours: number;
    en_attente_client: number;
    critiques_non_resolus: number;
    /** Délais moyens en heures : la mesure d'engagement de service. */
    delai_premiere_reponse_h: number | null;
    delai_resolution_h: number | null;
  };
}

export interface MessageTicket {
  id: string;
  message: string;
  interne: boolean;
  /** Une note privée n'est jamais renvoyée au client. */
  note_privee: boolean;
  created_at: IsoDateTime | null;
  auteur: { prenom: string | null; nom: string | null; email: string } | null;
}

// `demandeur` est REDÉFINI ici : sur la liste, l'e-mail est toujours connu ; sur le détail,
// le compte peut avoir été supprimé entre-temps et tous ses champs deviennent facultatifs.
// L'héritage exclut donc aussi `demandeur`, sinon les deux formes entrent en conflit.
export interface DetailTicketReponse
  extends Omit<LigneTicket, 'exploitation' | 'nb_messages' | 'demandeur'> {
  message: string | null;
  diagnostic: unknown;
  exploitation_id: Uuid | null;
  exploitation_nom: string | null;
  demandeur: { prenom: string | null; nom: string | null; email: string | null; telephone: string | null };
  appareil_sn: string | null;
  messages: MessageTicket[];
}

export interface ReponseTicketRequete {
  message: string;
  note_privee?: boolean;
}

export interface ChangementStatutTicketRequete {
  statut: StatutTicket;
  priorite?: PrioriteTicket;
  assigne_a_id?: Uuid;
}

/* -------------------------------------------------------------------------- */
/* Historique complet d'un appareil                                            */
/* GET /internal/v1/devices/{sn}/history/measures | frames | pulses            */
/* -------------------------------------------------------------------------- */

export interface FiltreHistoriqueRequete {
  /** Bornes incluses, ISO UTC. Sans `du` : tout l'historique. */
  du?: IsoDateTime;
  au?: IsoDateTime;
  curseur?: Curseur;
  limite?: number;
}

export interface FiltreHistoriqueTramesRequete extends FiltreHistoriqueRequete {
  type?: string;
  /** Texte recherché dans la charge utile. */
  recherche?: string;
}

export interface PageHistorique<T> {
  items: T[];
  /** Nombre de lignes sur la plage demandée, toutes pages confondues. */
  total: number;
  curseur_suivant: Curseur | null;
}

export interface LigneMesureHistorique {
  id: number;
  mesure_at: IsoDateTime;
  recu_at: IsoDateTime;
  tension_kv: Kilovolts;
  tension_min_kv: Kilovolts | null;
  tension_max_kv: Kilovolts | null;
  nb_impulsions: number;
  nb_impulsions_sous_seuil: number | null;
  nb_impulsions_absentes: number | null;
  source: 'PERIODIQUE' | 'SESSION_TEMPS_REEL';
  sous_seuil: boolean;
  message_id: string | null;
}

export interface LigneTrameHistorique {
  id: number;
  type: string;
  message_id: string;
  boot: number;
  seq: number;
  emis_at: IsoDateTime | null;
  recu_at: IsoDateTime;
  payload: Record<string, unknown>;
  signature_valide: boolean;
  traite_at: IsoDateTime | null;
  erreur_traitement: string | null;
  taille_octets: number | null;
}

export interface LigneImpulsionHistorique {
  id: number;
  commande_id: Uuid | null;
  nom_mesure: string | null;
  impulsion_at: IsoDateTime;
  recu_at: IsoDateTime;
  numero: number | null;
  intervalle_ms: number | null;
  tension_kv: Kilovolts | null;
  pic_lsb: number | null;
  lobe_positif_lsb: number | null;
  aire_lsb_ms: number | null;
  largeur_us: number | null;
  creux_us: number | null;
  nb_echantillons: number | null;
  ligne_base_lsb: number | null;
  seuil_lsb: number | null;
  bruit_lsb: number | null;
  nb_sat_bas: number | null;
  nb_sat_haut: number | null;
  rejetee: boolean;
  echantillons_s: number | null;
  kv_par_lsb_ms: number | null;
  /** 64 points espacés de 10 µs : écart brut à la ligne de base. */
  profil_court: number[];
  /** 50 paquets de 80 µs : écart moyen à la ligne de base sur la fenêtre de 4 ms. */
  profil_long: number[];
}

/* -------------------------------------------------------------------------- */
/* Étalonnage de la tension (firmware ≥ 2.5.1 : kV = coefficient × pic)        */
/* -------------------------------------------------------------------------- */

export interface SessionEtalonnage {
  commande_id: string;
  nom: string | null;
  debut_at: string;
  /** Impulsions retenues (rejets et fausses détections écartés). */
  nb: number;
  pic_moyen: number | null;
  pic_ecart: number | null;
  /** Écart moyen à la ligne de base sur 80-400 µs. */
  niveau_moyen: number | null;
  niveau_ecart: number | null;
  /** Méthode de calcul du firmware pendant la session : `aire` (≤ 2.4.x), `pic` ou `niveau`. */
  methode: string;
}

export interface EtalonnageReponse {
  /** Coefficient souhaité, en kV par LSB de pic ; `null` = valeur compilée du firmware. */
  kv_par_lsb: number | null;
  /** Grandeur convertie en kV (firmware ≥ 2.6.0). */
  kv_methode: MethodeKv;
  version: number | null;
  version_appliquee: number | null;
  statut_sync: string | null;
  derniere_sync_at?: string | null;
  /** Coefficient annoncé par la carte dans sa dernière mesure précise. */
  observe: { k: number | null; methode: string; at: string | null } | null;
  sessions: SessionEtalonnage[];
}

export type MethodeKv = 'pic' | 'niveau';

export interface EtalonnageRequete {
  kv_par_lsb: number | null;
  kv_methode?: MethodeKv;
  motif: string;
}

export interface EtalonnageResultat {
  kv_par_lsb: number | null;
  kv_methode: MethodeKv;
  version: number;
  configuration_publiee: boolean;
  executee_at: string;
}

/* -------------------------------------------------------------------------- */
/* Programmation USB d'une carte neuve                                         */
/* -------------------------------------------------------------------------- */

export interface MorceauFlash {
  nom: string;
  adresse: number;
  octets: number;
  sha256: string;
}

export interface DescriptionFlash {
  version: string;
  revision_carte: number | null;
  compile_at: string;
  morceaux: MorceauFlash[];
}

export interface ImageFlash extends DescriptionFlash {
  morceaux: (MorceauFlash & { donnees_b64: string })[];
}
